import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

export default function HubSpotCreate() {
  const navigate = useNavigate();
  const api = useApi();

  // OAuth flow state
  const [connecting, setConnecting] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Legacy PAT flow state
  const [showPat, setShowPat] = useState(false);
  const [patToken, setPatToken] = useState("");
  const [patName, setPatName] = useState("");
  const [patSaving, setPatSaving] = useState(false);
  const [patError, setPatError] = useState<string | null>(null);

  async function handleConnect() {
    setConnecting(true);
    setOauthError(null);
    try {
      const resp = await api.admin.get("HubSpot/OAuth/AuthUrl");
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setOauthError(body?.error ?? "Failed to start OAuth flow. Please try again.");
        return;
      }
      const data = await resp.json() as { authUrl: string };
      window.location.href = data.authUrl;
    } catch (ex) {
      console.error("HubSpot OAuth error", ex);
      setOauthError("Failed to start OAuth flow. Please try again.");
    } finally {
      setConnecting(false);
    }
  }

  async function handlePatSave() {
    if (!patToken.trim()) { setPatError("Access token is required."); return; }
    setPatSaving(true);
    setPatError(null);
    try {
      const resp = await api.admin.post("HubSpot/Configs", {
        displayName: patName.trim() || "HubSpot",
        accessToken: patToken.trim(),
        pushTranscripts: true,
        createContactIfMissing: false,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setPatError(body?.error ?? "Invalid token. Check and try again.");
        return;
      }
      navigate("/integrations/hubspot");
    } catch (ex) {
      console.error("HubSpot PAT save error", ex);
      setPatError("An unexpected error occurred.");
    } finally {
      setPatSaving(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4">

      {/* ── Primary: OAuth ── */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-orange-500" fill="currentColor">
              <path d="M22.449 12.416c0-.665-.06-1.305-.17-1.921H12v3.633h5.87a5.017 5.017 0 0 1-2.177 3.291v2.73h3.525c2.062-1.9 3.231-4.7 3.231-7.733z" fill="#4285F4"/>
              <path d="M12 23c2.943 0 5.408-1.005 7.211-2.72l-3.525-2.73c-.976.654-2.225 1.04-3.686 1.04-2.837 0-5.24-1.915-6.1-4.49H2.258v2.82A10.994 10.994 0 0 0 12 23z" fill="#34A853"/>
              <path d="M5.9 14.1A6.608 6.608 0 0 1 5.55 12c0-.736.126-1.452.35-2.1V7.08H2.258A10.994 10.994 0 0 0 1 12c0 1.773.425 3.45 1.258 4.92L5.9 14.1z" fill="#FBBC05"/>
              <path d="M12 5.41c1.598 0 3.032.549 4.16 1.627l3.12-3.12C17.4 2.145 14.938 1 12 1A10.994 10.994 0 0 0 2.258 7.08L5.9 9.9C6.76 7.325 9.163 5.41 12 5.41z" fill="#EA4335"/>
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-800">Connect with HubSpot</h2>
            <p className="text-xs text-slate-500">Recommended — secure OAuth 2.0, no token to manage</p>
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-5">
          You'll be redirected to HubSpot to authorize Velaro, then brought back automatically.
          Tokens are refreshed automatically — nothing expires on you.
        </p>

        {oauthError && <p className="text-red-600 text-sm mb-4">{oauthError}</p>}

        <div className="flex justify-end gap-2">
          <SecondaryBlueButton label="Cancel" onClick={() => navigate("/integrations/hubspot")} />
          {connecting ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <LoadingSpinner /> Redirecting to HubSpot…
            </div>
          ) : (
            <PrimaryBlueButton label="Connect with HubSpot" onClick={handleConnect} />
          )}
        </div>
      </div>

      {/* ── Legacy: Private App Token ── */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
        <button
          type="button"
          className="w-full flex items-center justify-between px-5 py-3 text-sm text-slate-400 hover:text-slate-500 transition"
          onClick={() => setShowPat((v) => !v)}
        >
          <span className="flex items-center gap-2">
            <span className="text-xs font-medium bg-slate-200 text-slate-500 px-2 py-0.5 rounded">Legacy</span>
            Private App Token
          </span>
          <svg className={`w-4 h-4 transition-transform ${showPat ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showPat && (
          <div className="px-5 pb-5 border-t border-slate-200">
            <p className="text-xs text-slate-400 mt-3 mb-4">
              HubSpot Private App Tokens work but are not required for marketplace listing.
              Use OAuth above unless you have a specific reason to use a token.
            </p>

            <label className="flex flex-col gap-1 mb-3">
              <span className="text-xs font-medium text-slate-500">Display Name</span>
              <input
                className="input text-sm"
                placeholder="e.g. Production HubSpot"
                value={patName}
                onChange={(e) => setPatName(e.target.value)}
              />
            </label>

            <label className="flex flex-col gap-1 mb-4">
              <span className="text-xs font-medium text-slate-500">Private App Access Token</span>
              <input
                className="input text-sm font-mono"
                placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={patToken}
                onChange={(e) => setPatToken(e.target.value)}
              />
              <span className="text-xs text-slate-400">Found in HubSpot → Settings → Integrations → Private Apps</span>
            </label>

            {patError && <p className="text-red-600 text-xs mb-3">{patError}</p>}

            <div className="flex justify-end">
              {patSaving ? (
                <div className="flex items-center gap-2 text-sm text-slate-500"><LoadingSpinner /> Saving…</div>
              ) : (
                <button
                  onClick={handlePatSave}
                  className="text-sm px-4 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 transition"
                >
                  Save Token
                </button>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
